import React, { useEffect, useState } from 'react';
import { MessageSquare, ShieldAlert, ArrowRight } from 'lucide-react';
import { api, DEMO } from '../lib/api';
import { hasCap } from '../lib/capabilities';
import { THREAD_STATUS, URGENCY, REQUEST_TYPES } from '../lib/clinicalMessages';
import SendToClinician from '../components/SendToClinician';
import { SendMessageInline } from './Dashboard';
import '../components/ClinicalMessages.css';

/**
 * Unified threaded inbox. Clinical roles see all threads (general + clinical requests);
 * front-desk (messages.use only) sees the general colleague pings. One messaging model.
 */
export default function ClinicalMessages({ user, notify, navigate, store, updateDemoStore, logDemo }) {
  const canClinical = hasCap(user, 'clinical_messages.view');
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [urgency, setUrgency] = useState('');

  const load = () => {
    if (DEMO) { setLoading(false); return; }
    const endpoint = canClinical ? '/clinical-message-threads' : '/messages';
    api(endpoint)
      .then(d => setThreads(d.threads || []))
      .catch(e => notify(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [user, canClinical]); // eslint-disable-line react-hooks/exhaustive-deps

  if (DEMO) {
    return <>
      <div className="page-heading"><div><div className="eyebrow">SECURE INTERNAL CLINICAL MESSAGING</div><h1>Clinical requests, in one thread.</h1><p>Coordinate PHP eligibility, certification, admission, and treatment decisions inside the facility workspace.</p></div></div>
      <div className="php-safety-alert" style={{ borderColor: '#b45309', background: '#fffbeb', color: '#92400e' }}><ShieldAlert size={20}/><div><strong>Clinical messaging requires live data.</strong><p>The threaded request model runs server-side, so the full inbox is available in the authenticated (non-demo) workspace.</p></div></div>
      {hasCap(user, 'messages.use') && <section className="content-card"><div className="content-heading"><div><h3>Notes between colleagues</h3><p>Quick, in-facility coordination (no patient record required).</p></div></div><SendMessageInline user={user} notify={notify} store={store} updateDemoStore={updateDemoStore} logDemo={logDemo} colleagues={null}/><div className="message-list">{(store.messages || []).map(m => <div className="message-row" key={m.id}><span className="file-icon"><MessageSquare size={17}/></span><div><strong>{m.sender_name || 'Colleague'}</strong><p>{m.body}</p><small>{new Date(m.created_at).toLocaleString()}</small></div></div>)}</div></section>}
    </>;
  }

  const q = query.toLowerCase();
  const filtered = threads.filter(t => {
    if (status && t.status !== status) return false;
    if (urgency && t.urgency !== urgency) return false;
    if (q && !`${t.subject || ''} ${t.patient_name || ''} ${REQUEST_TYPES[t.request_type] || ''}`.toLowerCase().includes(q)) return false;
    return true;
  });

  return <>
    <div className="page-heading"><div><div className="eyebrow">SECURE INTERNAL CLINICAL MESSAGING</div><h1>Clinical requests, in one thread.</h1><p>Coordinate PHP eligibility, certification, admission, and treatment decisions inside the facility workspace — no external email or texting of patient information.</p></div><div className="heading-actions">{canClinical && <SendToClinician user={user} notify={notify} navigate={navigate}/>}</div></div>

    {canClinical && <div className="cm-disclaimer"><ShieldAlert size={16}/><span>Clinical messaging is for internal coordination only. It is not an emergency response system — use emergency protocols for urgent clinical events. A chat “Approved” does not constitute a formal physician certification or signature.</span></div>}

    <section className="registry-panel practice-page">
      <div className="content-heading"><div><h3>Inbox <span className="badge neutral">{filtered.length}</span></h3><p>{canClinical ? 'Clinical requests and general messages you are part of.' : 'Notes between colleagues.'}</p></div></div>
      <div className="cm-filters" style={{ padding: '0 16px 12px' }}>
        <input aria-label="Search threads" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search patient, subject, or request type…"/>
        <select aria-label="Status" value={status} onChange={e => setStatus(e.target.value)}><option value="">All statuses</option>{Object.entries(THREAD_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>
        <select aria-label="Urgency" value={urgency} onChange={e => setUrgency(e.target.value)}><option value="">All urgencies</option>{Object.entries(URGENCY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>
      </div>

      {loading ? <div className="generation-skeleton"><i/><i/><i/></div> : !filtered.length ? <div className="empty-state"><MessageSquare size={28}/><h3>No requests yet</h3><p>Send a request to an authorized clinician to start a thread.</p></div> : (
        <div className="cm-list">
          {filtered.map(t => {
            const st = THREAD_STATUS[t.status] || { label: t.status, tone: 'neutral' };
            const ug = URGENCY[t.urgency] || { label: t.urgency, tone: 'neutral' };
            return <button key={t.id} className={`cm-row ${t.unread ? 'unread' : ''}`} onClick={() => navigate(`/messages/${t.id}`)}>
              <span className="file-icon"><MessageSquare size={17}/></span>
              <span className="cm-main">
                <span className="cm-title">
                  <strong>{t.subject || REQUEST_TYPES[t.request_type] || 'Clinical request'}</strong>
                  {t.patient_name && <span className="cm-badge neutral">{t.patient_name}</span>}
                  {t.request_type && t.request_type !== 'general' && <span className="cm-badge pending">{REQUEST_TYPES[t.request_type] || t.request_type}</span>}
                </span>
                <span className="cm-sub">{t.creator_name ? `From ${t.creator_name}` : ''}{t.assignee_name ? ` · assigned to ${t.assignee_name}` : ''}{t.last_activity ? ` · ${new Date(t.last_activity).toLocaleString()}` : ''}</span>
              </span>
              <span className="cm-meta">
                <span className="cm-badges"><span className={`cm-badge ${st.tone}`}>{st.label}</span><span className={`cm-badge ${ug.tone}`}>{ug.label}</span></span>
                <ArrowRight size={14}/>
              </span>
            </button>;
          })}
        </div>
      )}
    </section>
  </>;
}
